if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        const productInfo = this.closest('product-info');
        this.isMainProductMediaGallery = Boolean(
          productInfo?.id?.startsWith('MainProduct-') || this.closest('quick-add-modal')
        );
        this.updateGalleryViewerHeight = this.updateGalleryViewerHeight.bind(this);
        this.onGalleryNavigationButtonClick = this.onGalleryNavigationButtonClick.bind(this);
        this.bindGalleryNavigationButtons();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.updateGalleryViewerHeight();
        if (window.ResizeObserver) {
          this.galleryViewerResizeObserver = new ResizeObserver(this.updateGalleryViewerHeight);
          this.galleryViewerResizeObserver.observe(this.elements.viewer);
        } else {
          window.addEventListener('resize', this.updateGalleryViewerHeight);
        }
        window.addEventListener('load', this.updateGalleryViewerHeight, { once: true });
        if (this.isMainProductMediaGallery && this.dataset.desktopLayout.includes('thumbnail')) {
          this.preloadGalleryImages();
        }
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        event.detail.currentElement?.classList?.add('is-active');
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateGalleryNavigationButtons();
      }

      bindGalleryNavigationButtons() {
        if (!this.elements.viewer) return;
        this.galleryNavigationControls = this.elements.viewer.querySelector(':scope > .slider-buttons');
        if (!this.galleryNavigationControls) return;
        this.galleryNavigationButtons = {
          previous: this.galleryNavigationControls.querySelector('button[name="previous"]'),
          next: this.galleryNavigationControls.querySelector('button[name="next"]'),
        };
        Object.values(this.galleryNavigationButtons).forEach((button) => {
          button?.addEventListener('click', this.onGalleryNavigationButtonClick, true);
        });
        requestAnimationFrame(() => this.updateGalleryNavigationButtons());
        window.setTimeout(() => this.updateGalleryNavigationButtons(), 150);
      }

      async onGalleryNavigationButtonClick(event) {
        if (!this.isMainProductMediaGallery || !this.mql.matches || !this.dataset.desktopLayout.includes('thumbnail')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.isGallerySliding || this.isGalleryPreparing) return;

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const activeMediaId = activeMedia?.dataset.mediaId;
        const targetMedia = this.getAdjacentGalleryMedia(direction);
        if (!targetMedia) return;

        this.isGalleryPreparing = true;
        try {
          await this.prepareGalleryMedia(targetMedia);
          const currentActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
          if (!this.isConnected || currentActiveMedia?.dataset.mediaId !== activeMediaId) return;
          this.setActiveMedia(targetMedia.dataset.mediaId, false, direction);
        } finally {
          this.isGalleryPreparing = false;
        }
      }

      preloadGalleryImages() {
        const preload = () => {
          this.getGalleryMediaItems().forEach((mediaItem) => {
            const image = mediaItem.querySelector('img');
            if (image) this.queueGalleryImagePreload(image);
          });
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(preload, { timeout: 1500 });
        } else {
          window.setTimeout(preload, 600);
        }
      }

      queueGalleryImagePreload(image, priority = false) {
        if (!image) return;
        image.loading = 'eager';
        if ('fetchPriority' in image) image.fetchPriority = priority ? 'high' : 'auto';
        if (image.dataset.galleryPreloadQueued === 'true') return;
        image.dataset.galleryPreloadQueued = 'true';
        const source = image.currentSrc || image.src;
        if (!source) return;
        const preloader = new Image();
        if (image.sizes) preloader.sizes = image.sizes;
        if (image.srcset) preloader.srcset = image.srcset;
        if ('fetchPriority' in preloader) preloader.fetchPriority = priority ? 'high' : 'auto';
        preloader.src = source;
        this.galleryImagePreloads = this.galleryImagePreloads || [];
        this.galleryImagePreloads.push(preloader);
      }

      waitForGalleryImage(image, timeout = 1200) {
        if (!image) return Promise.resolve();
        this.queueGalleryImagePreload(image, true);
        const decodePromise =
          image.decode && image.currentSrc
            ? image.decode().catch(() => {})
            : new Promise((resolve) => {
                if (image.complete && image.naturalWidth > 0) {
                  resolve();
                  return;
                }
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
              });
        return Promise.race([decodePromise, new Promise((resolve) => window.setTimeout(resolve, timeout))]);
      }

      prepareGalleryMedia(mediaItem) {
        return this.waitForGalleryImage(mediaItem.querySelector('img'));
      }

      getGalleryMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      getAdjacentGalleryMedia(direction) {
        const mediaItems = this.getGalleryMediaItems();
        if (mediaItems.length < 2) return null;
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const activeIndex = Math.max(mediaItems.indexOf(activeMedia), 0);
        const targetIndex = activeIndex + direction;
        return mediaItems[targetIndex] || null;
      }

      updateGalleryNavigationButtons() {
        if (!this.galleryNavigationButtons || !this.galleryNavigationControls) return;
        const mediaItems = this.getGalleryMediaItems();
        const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const activeIndex = Math.max(mediaItems.indexOf(activeMedia), 0);

        this.galleryNavigationControls.hidden = mediaItems.length < 2;
        this.galleryNavigationButtons.previous?.toggleAttribute('disabled', activeIndex <= 0);
        this.galleryNavigationButtons.next?.toggleAttribute('disabled', activeIndex >= mediaItems.length - 1);
      }

      setActiveMedia(mediaId, prepend, slideDirection = 0) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const previousActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const shouldSlide =
          slideDirection && previousActiveMedia && previousActiveMedia !== activeMedia && this.mql.matches;
        if (shouldSlide) {
          this.animateMediaTransition(previousActiveMedia, activeMedia, slideDirection);
        } else {
          this.clearMediaTransition();
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateGalleryViewerHeight();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: slideDirection ? 'smooth' : 'auto' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        this.updateGalleryNavigationButtons();
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      animateMediaTransition(previousActiveMedia, activeMedia, direction) {
        this.clearMediaTransition();
        this.style.setProperty('--product-gallery-slide-enter', direction > 0 ? '100%' : '-100%');
        this.style.setProperty('--product-gallery-slide-exit', direction > 0 ? '-100%' : '100%');
        previousActiveMedia.classList.add('gallery-slide-from');
        activeMedia.classList.add('gallery-slide-to');
        this.classList.add('is-gallery-sliding');
        this.isGallerySliding = true;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.classList.add('is-gallery-slide-running');
          });
        });

        const finishTransition = () => this.clearMediaTransition();
        activeMedia.addEventListener('transitionend', finishTransition, { once: true });
        this.gallerySlideTimer = window.setTimeout(finishTransition, 760);
      }

      clearMediaTransition() {
        if (this.gallerySlideTimer) {
          window.clearTimeout(this.gallerySlideTimer);
          this.gallerySlideTimer = null;
        }
        this.classList.remove('is-gallery-sliding', 'is-gallery-slide-running');
        this.style.removeProperty('--product-gallery-slide-enter');
        this.style.removeProperty('--product-gallery-slide-exit');
        this.elements.viewer?.querySelectorAll('.gallery-slide-from, .gallery-slide-to').forEach((element) => {
          element.classList.remove('gallery-slide-from', 'gallery-slide-to');
        });
        this.isGallerySliding = false;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const thumbnailList = this.elements.thumbnails.slider;
        const isVertical = thumbnailList && window.getComputedStyle(thumbnailList).flexDirection === 'column';
        if (isVertical) {
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailList.scrollTop;
          const visibleBottom = visibleTop + thumbnailList.clientHeight;
          if (thumbnailTop < visibleTop || thumbnailBottom > visibleBottom) {
            thumbnailList.scrollTo({ top: Math.max(thumbnailTop - 10, 0), behavior: 'smooth' });
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      updateGalleryViewerHeight() {
        if (!this.elements.viewer) return;
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item') ||
          this.elements.viewer;
        const height = activeMedia.getBoundingClientRect().height || this.elements.viewer.getBoundingClientRect().height;
        if (height > 0) {
          this.style.setProperty('--product-gallery-viewer-height', `${height}px`);
          const thumbnailList = this.elements.thumbnails?.slider;
          if (!thumbnailList || window.getComputedStyle(thumbnailList).flexDirection !== 'column') return;
          this.style.setProperty('--product-thumbnail-gap', '1rem');
          const firstThumbnail = thumbnailList.querySelector('.thumbnail-list__item');
          const thumbnailRect = firstThumbnail?.getBoundingClientRect();
          const thumbnailRatio =
            thumbnailRect?.width > 0 && thumbnailRect?.height > 0 ? thumbnailRect.height / thumbnailRect.width : 1.09;
          const visibleThumbnails = 5;
          const gap = parseFloat(window.getComputedStyle(thumbnailList).gap) || 10;
          const thumbnailSize = (height - gap * (visibleThumbnails - 1)) / (visibleThumbnails * thumbnailRatio);
          if (thumbnailSize > 0) this.style.setProperty('--product-thumbnail-size', `${thumbnailSize}px`);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      disconnectedCallback() {
        if (this.galleryViewerResizeObserver) {
          this.galleryViewerResizeObserver.disconnect();
        } else if (this.updateGalleryViewerHeight) {
          window.removeEventListener('resize', this.updateGalleryViewerHeight);
        }
      }
    }
  );
}
